/**
 * Одна вилка отдельным окном.
 *
 * Зачем окно, а не блок на странице: пока человек заполняет форму ставки на
 * сайте конторы, сканер закрыт чужой вкладкой. Он возвращается за суммой второй
 * ноги, теряет строку в уехавшей ленте и ставит по цене, которой уже нет.
 * Отдельное окно кладут поверх конторы и держат перед глазами (08.08, владелец
 * — «поверх всех окон»). Прижать окно поверх прочих умеет система, а не сайт:
 * такого права браузер странице не даёт, и обещать его подписью было бы враньём.
 *
 * Внутри — та же карточка, что в ленте, буква в букву: две реализации одной
 * вилки однажды разойдутся в числах, а расходятся они всегда в пользу той, по
 * которой не ставили.
 *
 * Здесь нет ленты, фильтров и шапки. Место в окне стоит дороже всего, а решение
 * человек уже принял — он открыл ЭТУ вилку.
 *
 * Пустота «можно закрыть» запрещена: последняя строка остаётся, исчезновение обозначается
 * пометкой об устаревших данных. Закрыть окно может только человек.
 */

import { useEffect, useRef } from 'react';

import { Detail } from './Detail';
import { useHeldCalcRow } from './Calc';
import { calcOpen } from './pop';
import { useRows, type FeedRow } from './rows';
import { useSession } from '../access/session';
import { useSettings } from '../store/settings';
import { rememberOpened } from './opened';
import { useScreen } from './store';

import { coverMissing } from './workRow';

export function One({
  id,
  hold = 'open',
  frame,
}: {
  id: string;
  /**
   * `open` — отдельный документ `/app?fork=`: карточка и есть весь экран.
   * `pop` — окно из живой ленты: строку в ленте не раскрываем, вилку держим.
   */
  hold?: 'open' | 'pop';
  frame?: Window;
}): React.ReactElement {
  const meta = useScreen((s) => s.meta);
  const status = useScreen((s) => s.status);
  const framed = useScreen((s) => s.framed);
  const setOpen = useScreen((s) => s.setOpen);
  const pop = useScreen((s) => s.pop);
  const unpop = useScreen((s) => s.unpop);
  const { big, tail } = useRows();
  const held = useHeldCalcRow(id);
  const found = [...big, ...tail].find((one) => one.id === id) ?? null;
  const lastRef = useRef<FeedRow | null>(null);
  const missAt = useRef<number | null>(null);

  if (found !== null) {
    lastRef.current = found;
    if (found.ghost === null) missAt.current = null;
  } else if (lastRef.current?.id === id && missAt.current === null) {
    missAt.current = Date.now();
  }

  const row = coverMissing(
    found,
    lastRef.current?.id === id ? lastRef.current : null,
    missAt.current,
    status === 'live' && framed,
  ) ?? held.row;

  // Вилка считается взятой в работу: от этого зависит её удержание в кадре —
  // окно открыто именно ради неё, и пропасть она не имеет права. Значок «окном»
  // карточку в ленте не раскрывает: жест один и означает ровно одно (08.08).
  useEffect(() => {
    if (hold === 'pop') {
      pop(id);
      return () => {
        if (!calcOpen(id)) unpop(id);
      };
    }
    setOpen(id);
    return () => setOpen(null);
  }, [id, hold, pop, unpop, setOpen]);

  useEffect(() => {
    if (hold === 'pop' || found === null) return;
    rememberOpened(found, useScreen.getState().meta, useSettings.getState().fx, useSession.getState().me.id);
  }, [id, hold, found]);

  useEffect(() => {
    const parts = row?.row.fork.legs?.[0]?.participants ?? [];
    const title = parts.length > 0 ? parts.join(' — ') : 'вилка';
    (frame?.document ?? document).title = title;
  }, [row, frame]);

  if (row === null) {
    return (
      <div className="nx-one">
        <p className="nx-one__wait">
          {status !== 'live' || !framed ? 'подключаемся к движку…' : 'этой вилки в ленте нет'}
        </p>
      </div>
    );
  }

  return (
    <div className="nx-one">
      <Detail row={row} meta={meta} alone />

    </div>
  );
}
